import React, { useState, useCallback, useRef, useEffect, memo } from "react";
import { Loader2, Play, LayoutGrid, StickyNote, Terminal, ChevronDown, MoreHorizontal, Layers as LayersIcon } from "lucide-react";
import { useWorkflow } from "../../../context/WorkflowContext";
import { useWorkflowExecution } from "../../../context/WorkflowExecutionContext";
import { useCanvasContext } from "./WorkflowCanvasContext";
import CreateBlueprint from "../../components/CreateBlueprint";

export default function CanvasToolbar({ onShowExecution }) {
    const { steps } = useWorkflow();
    const { startWorkflow, stepIdRunning, isStartWorkflowLoading, isExecutingTask, stepExecutionResult } = useWorkflowExecution();
    const { autoLayout, addNote, selectedNodeId } = useCanvasContext();

    const [showRunMenu, setShowRunMenu] = useState(false);
    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const menuRef = useRef(null);
    const moreRef = useRef(null);
    const isRunning = !!stepIdRunning || isStartWorkflowLoading || isExecutingTask;

    // Determine if a step (not trigger) is selected
    const selectedStepIndex = selectedNodeId && !selectedNodeId.startsWith("trigger-")
        ? steps.findIndex((s) => s.id === selectedNodeId)
        : -1;
    const hasSelectedStep = selectedStepIndex !== -1;
    const selectedStep = hasSelectedStep ? steps[selectedStepIndex] : null;
    const isWebStep = selectedStep?.config?.language === "html" || selectedStep?.config?.language === "react";

    const handleRunAll = useCallback(() => {
        startWorkflow({}, null, null);
        onShowExecution();
        setShowRunMenu(false);
    }, [startWorkflow, onShowExecution]);

    const handleRunFromStep = useCallback((stepId) => {
        const stepIndex = steps.findIndex((s) => s.id === stepId);
        const prevResult = stepIndex > 0 && stepExecutionResult?.[stepIndex - 1]
            ? stepExecutionResult[stepIndex - 1]
            : {};
        startWorkflow(prevResult, stepId, null);
        onShowExecution();
        setShowRunMenu(false);
    }, [startWorkflow, onShowExecution, steps, stepExecutionResult]);

    const handleRunDefault = useCallback(() => {
        if (hasSelectedStep) {
            handleRunFromStep(selectedNodeId);
        } else {
            handleRunAll();
        }
    }, [hasSelectedStep, selectedNodeId, handleRunFromStep, handleRunAll]);

    useEffect(() => {
        if (!showRunMenu && !showMoreMenu) return;
        const handleClick = (e) => {
            if (showRunMenu && menuRef.current && !menuRef.current.contains(e.target)) setShowRunMenu(false);
            if (showMoreMenu && moreRef.current && !moreRef.current.contains(e.target)) setShowMoreMenu(false);
        };
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [showRunMenu, showMoreMenu]);

    return (
        <div className="h-12 flex items-center gap-1 px-2 border-b border-gray-200 bg-white flex-shrink-0">
            <div className="flex-1" />
            <div className="flex items-center gap-1">
                <button onClick={addNote} className="p-1.5 rounded-md hover:bg-yellow-50 text-gray-500 hover:text-yellow-600" title="Add note"><StickyNote size={15} /></button>
                <button onClick={autoLayout} className="p-1.5 rounded-md hover:bg-blue-50 text-gray-500 hover:text-blue-600" title="Auto-organize"><LayoutGrid size={15} /></button>
                <button onClick={onShowExecution} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700" title="Execution logs"><Terminal size={15} /></button>
                <div className="w-px h-5 bg-gray-200 mx-0.5" />
                <div className="relative" ref={menuRef}>
                    <div className="flex items-stretch rounded-md overflow-hidden h-7">
                        <button onClick={handleRunDefault} disabled={isRunning || isWebStep}
                            className={`flex items-center gap-1 px-2.5 text-white text-xs font-medium disabled:opacity-50 transition-colors ${
                                isWebStep ? "bg-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
                            }`}
                            title={isWebStep ? "Submit this step using the Preview tab" : undefined}>
                            {isRunning ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} fill="white" />}
                            {isRunning ? "Running..." : isWebStep ? "Use Preview to Submit" : hasSelectedStep ? `Run Step ${selectedStepIndex + 1}` : "Run"}
                        </button>
                        <button onClick={() => setShowRunMenu((v) => !v)} disabled={isRunning}
                            className="flex items-center px-1 bg-green-700 text-white hover:bg-green-800 disabled:opacity-50 transition-colors">
                            <ChevronDown size={13} />
                        </button>
                    </div>
                    {showRunMenu && (
                        <RunMenu steps={steps} onRunAll={handleRunAll} onRunFromStep={handleRunFromStep} selectedStepId={hasSelectedStep ? selectedNodeId : null} />
                    )}
                </div>
                <div className="relative" ref={moreRef}>
                    <button onClick={() => setShowMoreMenu((v) => !v)}
                        className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-gray-700" title="More options">
                        <MoreHorizontal size={15} />
                    </button>
                    {showMoreMenu && (
                        <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-30 py-1">
                            <CreateBlueprint renderTrigger={(open) => (
                                <button onClick={() => { open(); setShowMoreMenu(false); }}
                                    className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                    <LayersIcon size={13} /> Create Blueprint
                                </button>
                            )} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const RunMenu = memo(function RunMenu({ steps, onRunAll, onRunFromStep, selectedStepId }) {
    return (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-30 py-1">
            <button onClick={onRunAll} className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 font-medium">
                Run All Steps
            </button>
            <div className="h-px bg-gray-100 my-1" />
            {steps.map((step, idx) => {
                const isWeb = step.config?.language === "html" || step.config?.language === "react";
                return (
                    <button key={step.id} onClick={() => !isWeb && onRunFromStep(step.id)} disabled={isWeb}
                        className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
                            isWeb ? "text-gray-400 cursor-not-allowed" :
                            step.id === selectedStepId ? "text-green-700 font-medium bg-green-50 hover:bg-gray-50" : "text-gray-600 hover:bg-gray-50"
                        }`}
                        title={isWeb ? "Submit using Preview tab" : undefined}>
                        <span className={`w-4 text-right ${isWeb ? "text-gray-300" : step.id === selectedStepId ? "text-green-500" : "text-gray-400"}`}>{idx + 1}.</span>
                        <span className="truncate">{step.name || step.type}</span>
                        {isWeb && <span className="ml-auto text-[10px] text-gray-400">Preview</span>}
                    </button>
                );
            })}
        </div>
    );
});
